import { createDealerAxios } from '../factories/axios-factory';
import { useProposalStore } from '../stores/proposal-store';
import { useAuthStore } from '../stores/auth-store';

import { Vehicle, CapVehicle } from '../types/loan-application';
import useMockAlpheraProposal from '../functions/mocks/use-mock-alphera-proposal';
import useMockBnpProposal from '../functions/mocks/use-mock-bnp-proposal';
//@ts-ignore
import { calculateMonthlyPaymentForVehicle } from "@cf247/pricing/src/monthly-payment-calculator"
import { GlassesValuationResponse, CapValuationResponse, VehicleSearchResponse} from 'src/types/vehicle-search';

const dealerBackendDealer = createDealerAxios(process.env.DealersApiUrl!);

const vehicleService = {
  vehicleLookup(reg: string, mileage?: number) {
    return new Promise<Vehicle>(async (resolve, reject) => {

      let vehicle = null as Vehicle | null;

      // First try vehicle search
      const searchTask = dealerBackendDealer.get(
        `/vehicle/reg/internal?reg=${reg.toUpperCase().replace(" ", "")}`
      ).then(res => {
          if (res.data.vehicleId) {
            vehicle = {
              vehicleId: res.data.vehicleId,
              make: res.data.make,
              model: res.data.model,
              vehicleDateOfFirstReg: res.data.registrationDate ? new Date(res.data.registrationDate) : null,
              regYear: res.data.regYear,
              vehicleRegistration: res.data.regNumber,
              fuel: res.data.fuel,
              colour: res.data.colour,
              mileage: res.data.mileage,
              price: res.data.price,
              transmission: res.data.trans,
              isIntranetLoanVehicle: false,
              year: res.data.registrationDate ? new Date(res.data.registrationDate).getFullYear() : null,
              glassesRetailValuation: res.data.glassesRetailValuation,
              glassesTradeValuation: res.data.glassesTradeValuation,
              glassesRetailTransacted: res.data.glassesRetailTransacted,
              futures: res.data.vehicleResiduals ? JSON.parse(res.data.vehicleResiduals) : null,
              vehicleResiduals: res.data.vehicleResiduals,
              capCode: "",
              capRetail: res.data.capRetail,
              capAverage: res.data.capAverage,
              capClean: res.data.capClean,
              capCreatedOn: res.data.capCreatedOn,
              capId: res.data.capId,
              image: res.data.image,
              images: res.data.images,
              vin: res.data.vin,
            } as Vehicle;
          }
        }).catch(error => {
          console.log("no vehicle internal")
          vehicle = null;
        })

      await searchTask;

      let pcp = false;

      if (vehicle) {
        // check if pcp eligible
        const bnp = useMockBnpProposal(vehicle.price);
        const alphera = useMockAlpheraProposal(vehicle.price);

        const pcpVehicle = calculateMonthlyPaymentForVehicle(
          JSON.parse(JSON.stringify(vehicle)),
          [bnp, alphera],
          0,
          0,
          null,
          {
            annualMileage: useProposalStore().defaultPcpMileage,
            terms: [24, 36, 48, 60],
            ignorePcpLender:[49],
            ignoreLTV: true,
          }
        );

        pcp = pcpVehicle.pcpMatrix && pcpVehicle.pcpMatrix.length > 0 ? true : false;
      }
      else if (mileage && mileage < 80000) {
        pcp = true;
      }
      else {
        pcp = false;
      }

      // Do a cap lookup
      const capTask = dealerBackendDealer.get(
        `/vehicle-cap/${reg}/${pcp}/${useAuthStore().isIntranetUser ? 'CF247-User-Dealer-Portal' : (useAuthStore().affiliateUserId ?? useAuthStore().affiliateUserIds[0]) }`
      ).then(async (res) => {
        const capVehicle = res.data as CapVehicle;

        if(vehicle && !vehicle.vehicleDateOfFirstReg && capVehicle && capVehicle.vehicleDateOfFirstReg){
          vehicle.vehicleDateOfFirstReg = capVehicle.vehicleDateOfFirstReg;
        }

        // Rare cases of reg switch, cap lookup for a reg will return
        // a different reg with no capcode, researching by that reg
        // returns a cap code
        if (vehicle && capVehicle.capCode === null) {
          const response = await dealerBackendDealer.get(
            `/vehicle-cap/${capVehicle.vehicleRegistration}/${pcp}/${useAuthStore().isIntranetUser ? 'CF247-User-Dealer-Portal' : (useAuthStore().affiliateUserId ?? useAuthStore().affiliateUserIds[0])}`
          )

          const regSwitch = response.data as Vehicle;
          if (vehicle && !vehicle.capCode) {
            vehicle.capCode = capVehicle.capCode;//
          }
        }
        // if just missing capcode, use that
        else if (vehicle) {
          vehicle.capCode = capVehicle.capCode;
        }
        else {
          // Use the cap lookup vehicle
          if (capVehicle && capVehicle.vehicleDateOfFirstReg) {
            capVehicle.vehicleDateOfFirstReg = new Date(capVehicle.vehicleDateOfFirstReg);
          }
          // Some rare cases of cap lookup only returning a date of reg, no make / model
          // Do not allow to quote
          if (capVehicle.make) {
            vehicle = {
              vehicleId: capVehicle.vehicleId,
              make: capVehicle.make,
              model: capVehicle.model,
              mileage: capVehicle.mileage,
              vehicleRegistration: capVehicle.vehicleRegistration,
              vehicleDateOfFirstReg: capVehicle.vehicleDateOfFirstReg,
              price: capVehicle.price,
              vehicleCondition: capVehicle.vehicleCondition,
              fuel: capVehicle.fuelType,
              transmission: capVehicle.transmission,
              annualMileage: capVehicle.annualMileage,
              capId: capVehicle.capId,
              capCode: capVehicle.capCode,
              capCreatedOn: capVehicle.capCreatedOn ? new Date(capVehicle.capCreatedOn) : null,
              capAverage: capVehicle.capAverage,
              capClean: capVehicle.capClean,
              capRetail: capVehicle.capRetail,
              colour: capVehicle.colour,
              glassCode: capVehicle.glassCode,
              glassesEffectiveFrom: capVehicle.glassesEffectiveFrom ? new Date(capVehicle.glassesEffectiveFrom) : null,
              glassesRetailTransacted: capVehicle.glassesRetailTransacted,
              glassesRetailValuation: capVehicle.glassesRetailValuation,
              glassesTradeValuation: capVehicle.glassesTradeValuation,
              vin: capVehicle.vin,
              isIntranetLoanVehicle: true
            } as Vehicle;
          }
        }
      }).catch(error => {
        console.log("no vehicle external");
      })

      await capTask;
 
      if (vehicle) {
        vehicle.image = 'https://res.cloudinary.com/carfinance247-co-uk/image/upload/q_10/v1/' + vehicle.vehicleId + '/1.webp'

        resolve(vehicle);
      }

      reject(null);
     
    })
  },
  glassesValuation(reg: string, mileage: number) {
    return new Promise<GlassesValuationResponse>((resolve, reject) => {
      dealerBackendDealer.get(
        `/glasses-valuations?vehicleRegistration=${reg.toUpperCase().replace(" ", "")}&vehicleMileage=${mileage}`).then(res => {
          if (res.status === 200) {
            
            if(res.data && res.data.createdOn && res.data.createdOn === "0001-01-01T00:00:00"){ 
              res.data.createdOn = new Date();
            }

            resolve(res.data as GlassesValuationResponse);
          }
        }).catch(error => {
          console.log("no valuations found!");
          reject(null)
        })
    })
  },
  capValuation(capId: number, mileage: number, reg: string, year: number | undefined) {
    return new Promise<CapValuationResponse>((resolve, reject) => {
      dealerBackendDealer.get(
        `/cap-valuations?capId=${capId}&mileage=${mileage}&vehicleRegistration=${reg.toUpperCase().replace(" ", "")}&year=${year}`).then(res => {
          if (res.status === 200) {
            resolve(res.data as CapValuationResponse);
          }
        }).catch(error => {
          console.log("no valuations found!");
          reject(null)
        })
    })
  },
  getStockVehicles(dealerId: number) {
    // use smartfish feed for wilds and coles
    if (dealerId === 46485) {
      dealerId = 44894;
    }
    if (dealerId === 50235) {
      dealerId = 38539;
    }

    
    return new Promise<VehicleSearchResponse>((resolve, reject) => {
      dealerBackendDealer.post(
        `/vehicle/stock`,
        {
          postcode: null,
          mileage: null,
          transId: null,
          priceMin: null,
          priceMax: null,
          engineMin: null,
          engineMax: null,
          colourIds: [],
          dealerId,
          pageSize: 999,
          page: 1,
          searchPublisherIds: [5]
        }
      ).then(res => {
        const r = res.data;
        const mapped = [] as Vehicle[];
        r.vehicles.forEach((v: any) => {
          mapped.push({
            vehicleId: v.vehicleId,
            make: v.make,
            model: v.model,
            trim: v.trim,
            vehicleDateOfFirstReg: v.registrationDate !== null ?
              new Date(v.registrationDate) :
              v.year ?
                new Date(v.year, new Date().getMonth(), new Date().getDate()) :
                v.regYear && v.regYear.toString().length === 2 ?
                  regDateFromRegYear(v.regYear) :
                  null,
            regYear: v.regYear,
            vehicleRegistration: v.regNumber,
            fuel: v.fuel,
            colour: v.colour,
            mileage: v.mileage,
            price: v.price,
            transmission: v.trans,
            isIntranetLoanVehicle: false,
            images: v.images,
            image: v.image,
            dealerId: v.dealerId,
            year: v.registrationDate ?
              new Date(v.registrationDate).getFullYear() :
              v.year ?
                v.year :
                v.regYear ?
                  regDateFromRegYear(v.regYear)!.getFullYear() :
                  null
          } as Vehicle);
        })

        resolve({
          pageSize: res.data.pageSize,
          page: res.data.page,
          total: res.data.total,
          vehicles: mapped
        })
      }).catch(error => {
        reject(error);
      })
    })
  }
};

function regDateFromRegYear(regYear: number) {
  const split = regYear.toString().split("");
  switch (split[0]) {
    case "0": // 2000s mar-aug
      return new Date(Number("200" + split[1]), 3, 1);
    case "5": // 2000s sep+
      return new Date(Number("200" + split[1]), 9, 1);
    case "1": // 2010s mar-aug
      return new Date(Number("201" + split[1]), 3, 1);
    case "6": // 2010s sep+
      return new Date(Number("201" + split[1]), 9, 1);
    case "2": // 2020s mar-aug
      return new Date(Number("202" + split[1]), 3, 1);
    case "7": // 2020s sep+
      return new Date(Number("202" + split[1]), 9, 1);
  }
}

export default vehicleService;
